import { useEffect, useMemo, useState } from "react";
import { PiCaretDownBold } from "react-icons/pi";

import FilterCheckbox from "../FilterSection/shared/FilterCheckbox";
import { cn } from "@/lib/utils";
import {
  useTracksControllerTracksListGenres,
  useTracksControllerTracksListSubGenres,
} from "@/lib/lutely-api/gen/tracks/tracks";
import type { ClassificationDto } from "@/lib/lutely-api/gen/dtos/classificationDto";

type MobileGenreFilterProps = {
  value: string[];
  onChange: (next: string[]) => void;
};

export default function MobileGenreFilter({
  value,
  onChange,
}: MobileGenreFilterProps) {
  const { data: genresData = [] } = useTracksControllerTracksListGenres();
  const [expandedGenre, setExpandedGenre] = useState<string | null>(null);
  const [subgenreCache, setSubgenreCache] = useState<
    Record<string, ClassificationDto[]>
  >({});
  const [subgenreParentMap, setSubgenreParentMap] = useState<
    Record<string, string>
  >({});

  const activeGenreId = expandedGenre ?? null;
  const { data: fetchedSubgenres = [] } =
    useTracksControllerTracksListSubGenres(activeGenreId ?? "", {
      query: {
        enabled: Boolean(activeGenreId),
      },
    });

  useEffect(() => {
    if (activeGenreId && fetchedSubgenres.length > 0) {
      setSubgenreCache((prev) => ({
        ...prev,
        [activeGenreId]: fetchedSubgenres,
      }));

      setSubgenreParentMap((prev) => {
        const next = { ...prev };
        fetchedSubgenres.forEach((subgenre) => {
          next[subgenre.classificationId] = activeGenreId;
        });
        return next;
      });
    }
  }, [activeGenreId, fetchedSubgenres]);

  const resolvedSubgenres = useMemo(() => {
    if (!activeGenreId) {
      return [];
    }
    return subgenreCache[activeGenreId] || fetchedSubgenres || [];
  }, [activeGenreId, fetchedSubgenres, subgenreCache]);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const toggleSelection = (id: string) => {
    if (selectedSet.has(id)) {
      onChange(value.filter((item) => item !== id));
    } else {
      onChange([...value, id]);
    }
  };

  const hasSelectedSubgenre = (genreId: string) => {
    for (const subgenreId of value) {
      if (subgenreParentMap[subgenreId] === genreId) {
        return true;
      }
    }
    return false;
  };

  return (
    <div className="space-y-4">
      <div className="divide-y divide-white/5 overflow-hidden rounded-2xl">
        {genresData.map((genre) => {
          const isActive = activeGenreId === genre.classificationId;
          const isSelected = selectedSet.has(genre.classificationId);
          const showIndicator =
            !isSelected && hasSelectedSubgenre(genre.classificationId);

          return (
            <button
              key={genre.classificationId}
              type="button"
              onClick={() =>
                setExpandedGenre((prev) =>
                  prev === genre.classificationId
                    ? null
                    : genre.classificationId,
                )
              }
              className={cn(
                "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors",
                isActive ? "bg-white/5" : "",
              )}
            >
              <button
                type="button"
                className="shrink-0"
                onClick={(event) => {
                  event.stopPropagation();
                  toggleSelection(genre.classificationId);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    toggleSelection(genre.classificationId);
                  }
                }}
              >
                <FilterCheckbox checked={isSelected} />
              </button>
              <span className="flex-1 font-semibold text-[15px]">
                {genre.name}
              </span>
              {showIndicator && (
                <span className="inline-flex h-2 w-2 rounded-full bg-brand-primary" />
              )}
              <PiCaretDownBold
                className={cn(
                  "text-white/60 transition-transform",
                  isActive ? "rotate-180" : "",
                )}
              />
            </button>
          );
        })}
      </div>

      <div className="rounded-2xl p-4">
        <p className="text-[13px] font-semibold text-white/70 mb-3">
          Subgenres
        </p>
        {!activeGenreId ? (
          <p className="text-white/60 text-[13px]">
            Select a genre to view subgenres.
          </p>
        ) : resolvedSubgenres.length === 0 ? (
          <p className="text-white/60 text-[13px]">No subgenres available.</p>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {resolvedSubgenres.map((subgenre) => {
              const isSelected = selectedSet.has(subgenre.classificationId);
              return (
                <button
                  key={subgenre.classificationId}
                  type="button"
                  onClick={() => toggleSelection(subgenre.classificationId)}
                  className={cn(
                    "flex items-center gap-2 rounded-lg px-3 py-2 bg-white/0 hover:bg-white/10 transition-colors",
                    isSelected && "bg-white/10",
                  )}
                >
                  <FilterCheckbox checked={isSelected} />
                  <span className="text-left text-[14px] font-medium">
                    {subgenre.name}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
